import { useCallback, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import ListActionBar from 'src/components/ListActionBar';
import NoDataCell from 'src/components/NoDataCell';
import StatusChip from 'src/components/StatusChip';
import LoadingRows from 'src/components/Table/LoadingRows';
import TableContainer from 'src/components/Table/TableContainer';
import { useInfiniteDeploymentRingDevices } from 'src/hooks/deploymentRing';
import { DEVICES_PREVIOUS_PATH } from 'src/pages/devices/deviceDetails/constant';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { DEVICE_STATUS, getDeviceStatusLabel } from 'src/utils/enums/deviceStatus';
import { getHealthStatusColor, getHealthStatusLabel } from 'src/utils/enums/healthStatus';
import { getWdacStatusLabel } from 'src/utils/enums/wdacStatus';
import { formatDate } from 'src/utils/formatTime';

const PAGE_LIMIT = 50;
const TABLE_MAX_HEIGHT = 'calc(100vh - 320px)';
const DEFAULT_SORT = { column: 'lastContactDate', direction: 'desc' };

// Like the devices list, the active devices are the ones worth seeing first; the rest are a filter
// away. The filter lives in the URL so it survives opening a device and coming back.
const DEFAULT_FILTERS = { statuses: [DEVICE_STATUS.Approved, DEVICE_STATUS.Unknown] };

const FILTER_OPTIONS = [
  {
    key: 'statuses',
    type: 'checkbox',
    label: 'Device Status',
    options: [
      { value: DEVICE_STATUS.Approved, label: 'Approved' },
      { value: DEVICE_STATUS.Revoked, label: 'Revoked' },
      { value: DEVICE_STATUS.Archived, label: 'Archived' },
      { value: DEVICE_STATUS.Unknown, label: 'No Status' },
    ],
  },
];

const COLUMNS = [
  { key: 'name', label: 'Device', sortKey: 'name', width: '28%' },
  { key: 'status', label: 'Status', width: '12%' },
  { key: 'healthStatus', label: 'Health Status', width: '14%' },
  { key: 'lastContactDate', label: 'Last Contact', sortKey: 'lastContactDate', width: '16%' },
  { key: 'wdacDeviceStatus', label: 'WDAC Status', width: '14%' },
  { key: 'osVersion', label: 'OS Version', width: '16%' },
];

const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': { fontSize: '0.8125rem' },
};

const renderCell = (column, device) => {
  switch (column.key) {
    case 'name':
      return (
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {device.name}
        </Typography>
      );
    case 'status':
      return <Typography variant="caption">{getDeviceStatusLabel(device.status)}</Typography>;
    case 'healthStatus': {
      const color = getHealthStatusColor(device.healthStatus);

      return (
        <StatusChip
          label={getHealthStatusLabel(device.healthStatus)}
          color={color}
          backgroundColor={`${color}1F`}
        />
      );
    }
    case 'lastContactDate':
      return <Typography variant="caption">{formatDate(device.lastContactDate) || '--'}</Typography>;
    case 'wdacDeviceStatus':
      return <Typography variant="caption">{getWdacStatusLabel(device.wdacDeviceStatus)}</Typography>;
    default:
      return <Typography variant="caption">{device[column.key] || '--'}</Typography>;
  }
};

const DeploymentRingDetailsDevices = () => {
  const navigate = useNavigate();
  const { ringId } = useParams();

  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [filters, setFilters] = useState(
    () => readFilterParams(DEFAULT_FILTERS, FILTER_OPTIONS) ?? DEFAULT_FILTERS,
  );

  useFilterParams(filters);

  const trimmedSearchValue = useMemo(() => searchValue.trim(), [searchValue]);

  const apiQuery = useMemo(
    () => ({
      ...(trimmedSearchValue ? { Search: trimmedSearchValue } : {}),
      ...(filters.statuses.length ? { Statuses: filters.statuses } : {}),
      SortColumn: sort.column,
      SortDesc: sort.direction === 'desc',
    }),
    [trimmedSearchValue, filters.statuses, sort],
  );

  const handleFilterChange = useCallback(
    nextFilters => setFilters({ statuses: nextFilters.statuses || [] }),
    [],
  );

  const devicesQuery = useInfiniteDeploymentRingDevices({
    ringId,
    apiQuery,
    limit: PAGE_LIMIT,
  });

  const devices = useMemo(
    () => devicesQuery.data?.pages?.flatMap(page => page.devices || []) || [],
    [devicesQuery.data],
  );
  const totalCount = devicesQuery.data?.pages?.[0]?.count ?? 0;

  const loadMore = useCallback(() => {
    if (devicesQuery.hasNextPage) {
      devicesQuery.fetchNextPage();
    }
  }, [devicesQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: devicesQuery.isFetchingNextPage,
    hasNextPage: devicesQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const handleSort = useCallback(column => {
    if (!column.sortKey) {
      return;
    }

    setSort(prev =>
      prev.column === column.sortKey
        ? { column: column.sortKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column: column.sortKey, direction: 'asc' },
    );
  }, []);

  // The device page's back button returns to wherever it was opened from, so hand it this tab.
  const handleRowClick = useCallback(
    deviceId => {
      sessionStorage.setItem(
        DEVICES_PREVIOUS_PATH,
        `${window.location.pathname}${window.location.search}`,
      );
      navigate(`${PORTAL_ROUTE}/devices/${deviceId}/general`);
    },
    [navigate],
  );

  return (
    <Box sx={{ position: 'relative', mt: 2 }}>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        onResetSearch={() => setSearchValue('')}
        searchPlaceholder="Device name"
        filters={filters}
        onFilterChange={handleFilterChange}
        filterOptions={FILTER_OPTIONS}
        filterChips
        filterButtonCount={totalCount}
      />

      <TableContainer roundedTop styles={{ maxHeight: TABLE_MAX_HEIGHT }}>
        <Table stickyHeader size="small" sx={TABLE_SX}>
          <colgroup>
            {COLUMNS.map(column => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3F3F3F' }}>
            <TableRow>
              {COLUMNS.map(column => {
                const isSorted = sort.column === column.sortKey;

                return (
                  <TableCell key={column.key} sx={{ p: '8px 16px' }}>
                    <Stack
                      direction="row"
                      gap={0.5}
                      alignItems="center"
                      onClick={column.sortKey ? () => handleSort(column) : undefined}
                      sx={{ cursor: column.sortKey ? 'pointer' : 'default' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#DDDDDD' }}>
                        {column.label}
                      </Typography>
                      {column.sortKey ? (
                        <Iconify
                          icon={
                            isSorted
                              ? sort.direction === 'asc'
                                ? 'mdi:arrow-up'
                                : 'mdi:arrow-down'
                              : 'mdi:swap-vertical'
                          }
                          width={14}
                          height={14}
                          color={isSorted ? '#7DFF92' : '#9CA3AF'}
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {devicesQuery.isLoading ? (
              <LoadingRows columnCount={COLUMNS.length} rowCount={8} />
            ) : (
              <>
                {devices.map((device, index) => (
                  <TableRow
                    key={device.id}
                    ref={index === devices.length - 1 ? sentryRef : null}
                    hover
                    onClick={() => handleRowClick(device.id)}
                    sx={{ cursor: 'pointer' }}>
                    {COLUMNS.map(column => (
                      <TableCell key={`${device.id}-${column.key}`} sx={{ py: 0.75 }}>
                        {renderCell(column, device)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {devicesQuery.isFetchingNextPage ? (
                  <LoadingRows columnCount={COLUMNS.length} rowCount={3} />
                ) : null}
                {!devicesQuery.isFetchingNextPage && !devices.length ? (
                  <NoDataCell colSpan={COLUMNS.length}>
                    No devices are assigned to this deployment ring.
                  </NoDataCell>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DeploymentRingDetailsDevices;
